import { Radio, TableProps, Tooltip } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import { formatPercentValue } from '../../../foundation/utils/percentageHandler';
import { useAppDispatch } from '../../../store/hooks';
import {
  useGetFrequencyRatiosQuery,
  useGetMaxAFScoringsQuery,
  useSetAsDefaultMaxCAFScoringMutation,
} from '../../api/variantApi';
import { VARIANT_STAGE_TYPES } from '../constants';
import { useVariantStep } from '../hooks/use_variant_step';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { setFrequencyDataNeedsRecalculation } from '../redux';
import { MaxAFScoring } from '../redux';
import MaximumCredibleModal from './MaximumCredibleModal';

const MaximumCredibleSection = () => {
  const dispatch = useAppDispatch();
  const params = useParams();

  const [isMaximumCredibleModalVisible, setIsMaximumCredibleModalVisible] =
    useState(false);

  const [selectedRecord, setSelectedRecord] = useState<
    MaxAFScoring | undefined
  >(undefined);

  const { data: ratios } = useGetFrequencyRatiosQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  // Read max-AF scorings straight from the query hook's cache rather than a selector
  const { data: maxAFScorings } = useGetMaxAFScoringsQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  const { canEditWorkflow } = useVariantAccess();
  const stageInfo = useVariantStep(VARIANT_STAGE_TYPES.FREQUENCY_DATA);
  const [setAsDefault] = useSetAsDefaultMaxCAFScoringMutation();
  const isThresholdEditDisabled =
    !canEditWorkflow ||
    (stageInfo?.status !== 'revised' && stageInfo?.status !== 'not_started');
  const dataSource = maxAFScorings ?? [];

  const defaultMaxAFScoring = useMemo(
    () => dataSource.find((item) => item.is_default) ?? dataSource[0] ?? null,
    [dataSource],
  );

  const handleSetAsDefault = useCallback(
    (record: MaxAFScoring) => {
      if (!(isThresholdEditDisabled || !params.vid)) {
        setAsDefault({ id: record.id, variant_id: params.vid });
        dispatch(setFrequencyDataNeedsRecalculation(true));
      } else {
        return;
      }
    },
    [isThresholdEditDisabled, params.vid, setAsDefault, dispatch],
  );

  const renderSetAsDefault = useCallback(
    (_value: boolean, record: MaxAFScoring) => {
      const isDefault = defaultMaxAFScoring?.id === record.id;
      return (
        <div className="h-table-action-col">
          <span style={{ display: 'inline-block' }}>
            <Radio
              checked={isDefault}
              disabled={isDefault || isThresholdEditDisabled}
              onChange={() => handleSetAsDefault(record)}
            />
          </span>
        </div>
      );
    },
    [defaultMaxAFScoring, isThresholdEditDisabled, handleSetAsDefault],
  );

  const handleMaximumCredibleModalOpen = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, record: MaxAFScoring) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedRecord(record);
      setIsMaximumCredibleModalVisible(true);
    },
    [setSelectedRecord],
  );

  const handleMaximumCredibleModalClose = useCallback(() => {
    setIsMaximumCredibleModalVisible(false);
    setSelectedRecord(undefined);
  }, [setSelectedRecord]);

  const columns: TableProps<MaxAFScoring>['columns'] = useMemo(
    () => [
      {
        title: 'Associated phenotype',
        dataIndex: 'associated_phenotype',
        key: 'associated_phenotype',
        render: (text, record) => (
          <a
            className="clinical-obs__report-category-link"
            rel="noopener noreferrer"
            onClick={(e) => handleMaximumCredibleModalOpen(e, record)}
          >
            <strong className="h-bold-column">{text?.preferred_term}</strong>
          </a>
        ),
      },
      {
        title: 'Inheritance',
        dataIndex: 'inheritance',
        key: 'inheritance',
      },
      {
        title: 'maxcAC',
        dataIndex: 'maxc_ac',
        key: 'maxc_ac',
      },
      {
        title: 'maxcAF [%]',
        dataIndex: 'maxc_af',
        key: 'maxc_af',
        render: (value: string) => formatPercentValue(parseFloat(value)),
      },
      {
        title: 'estimated AF [%]',
        dataIndex: 'estimated_af',
        key: 'estimated_af',
        render: (value: string) => formatPercentValue(parseFloat(value)),
      },
      {
        title: 'Set as threshold',
        dataIndex: 'is_default',
        key: 'is_default',
        render: renderSetAsDefault,
      },
    ],
    [renderSetAsDefault, handleMaximumCredibleModalOpen],
  );

  return (
    <>
      {isMaximumCredibleModalVisible && (
        <MaximumCredibleModal
          closeModal={handleMaximumCredibleModalClose}
          record={selectedRecord}
        />
      )}
      <div className="frequency-data__max-credible-section">
        <div className="frequency-data__max-credible-header">
          Analysis of maximal credible allele frequency
          {ratios?.faf_daf_ratio && (
            <Tooltip
              title={ratios.faf_daf_ratio.tooltip}
              overlayClassName="molecular-impact__tooltip"
              placement="bottom"
            >
              <span className="frequency-data__ratio-badge">
                {ratios.faf_daf_ratio.label}
              </span>
            </Tooltip>
          )}
        </div>
        <ResizableTable
          pagination={false}
          columns={columns}
          dataSource={dataSource}
          rowKey={(record) => record.id}
          persistenceKey="max-credible-af"
        />
      </div>
    </>
  );
};

export default MaximumCredibleSection;
